import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

import { formatClockMinutes, parseClockMinutes } from '@nightshift/notifications';
import type { UiConfigResponse } from '@nightshift/ui-types';

import { Empty, Kicker, Spinner } from '../components/primitives.tsx';
import {
  DEFAULT_SETTINGS_TAB,
  SETTINGS_SCOPES,
  resolveSettingsTab,
  settingsTab,
  settingsTabsForScope,
  type SettingsScope,
  type SettingsTabId,
} from '../lib/settings-tabs.ts';
import { UI_SCALE_OPTIONS, activityDisplay, setActivityDisplay, setTimeFormat, setUiScale, timeFormat, uiScale } from '../lib/display.ts';
import { TIME_FORMATS } from '../lib/format.ts';
import { SettingsAutosaveNotice } from '../lib/settings-autosave.tsx';
import {
  backgroundNotificationPermission,
  backgroundNotificationPrefs,
  requestBackgroundNotificationPermission,
  setBackgroundNotificationPrefs,
} from '../lib/notify.ts';
import { api, type SessionUser } from '../live/api.ts';
import { useSlice, useStore } from '../live/context.tsx';
import type { LiveState } from '../live/store.ts';
import { canWriteSettings } from '../lib/privilege.ts';
import { PromptsPanel } from './prompts-settings.tsx';
import { ReviewersPanel } from './reviewers-settings.tsx';
import { PerformanceTargetsPanel } from './performance-targets-settings.tsx';
import { ProjectSettings } from './project-settings.tsx';
import { ProjectConfigurationPanel } from './project-configuration-settings.tsx';
import { ProfilesPanel } from './profiles-settings.tsx';
import { MachinesSettingsPanel } from './machines-settings.tsx';
import { MilestonesSettingsPanel } from './milestones-settings.tsx';
import { ModelsSettingsPanel } from './models-settings.tsx';
import { RoutesSettingsPanel } from './routes-settings.tsx';
import { SidecarsSettingsPanel } from './sidecars-settings.tsx';
import { UsersPanel } from './users-settings.tsx';
import { ConnectPanel } from './connect-settings.tsx';
import './sheet.css';
import './settings.css';

/**
 * The settings screen.
 *
 * Each project has one configuration document stored by Nightshift; the Project categories
 * edit it through structured controls, and nothing else supplies project configuration.
 *
 * Layout: a side navigation grouped by scope — Project, Nightshift, This browser — with the
 * categories listed under each. The first thing an operator wants to know about a setting is
 * who it reaches, so the scope is the heading and the category sits beneath it, and the page
 * header restates both. The earlier version had a scope switch and a flat row of thirteen
 * category tabs that did not visibly belong to it; switching scope silently jumped the tab
 * row, and the row overflowed at 1440. Categories are still addressed by `?group=`, so every
 * deep link that shipped keeps working.
 */

type TabId = SettingsTabId;

const selectProjectSlug = (state: LiveState) => state.projectSlug;

/** What a scope's changes land on, as the one token shown under its heading. */
function scopeTarget(scope: SettingsScope, projectSlug: string | null): string {
  if (scope === 'project') return projectSlug ?? 'no project selected';
  if (scope === 'nightshift') return 'all projects';
  return 'this browser';
}

export function SettingsView({
  selectedGroup,
  onSelectGroup,
  user,
}: {
  /** Which config group is open, from the URL. */
  selectedGroup: string | null;
  onSelectGroup: (group: string | null) => void;
  user: SessionUser | null;
}) {
  const projectSlug = useSlice(selectProjectSlug);
  const [config, setConfig] = useState<UiConfigResponse | null>(null);
  const [loadedProject, setLoadedProject] = useState<string | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const loadSequence = useRef(0);
  /*
   * The URL owns the open group.
   *
   * A configuration screen has to be linkable: a warning that says "your concurrency cap is
   * doing this" is worth nothing if it cannot send the operator to the concurrency group.
   * Local state also made the group impossible to screenshot reproducibly, which is how four
   * capture shots of four different groups all came out as the default one.
   */
  const tab: TabId = resolveSettingsTab(selectedGroup) ?? DEFAULT_SETTINGS_TAB;
  const setTab = (next: TabId) => onSelectGroup(next === DEFAULT_SETTINGS_TAB ? null : next);
  const meta = settingsTab(tab);
  const scopeMeta = SETTINGS_SCOPES.find((scope) => scope.id === meta.scope)!;
  const canWrite = canWriteSettings(user);

  const load = useCallback(async () => {
    const requestedProject = projectSlug;
    const sequence = ++loadSequence.current;
    try {
      const response = await api.config(requestedProject);
      if (sequence !== loadSequence.current) return;
      setConfig(response);
      setLoadedProject(requestedProject);
      setError(null);
    } catch (caught) {
      if (sequence !== loadSequence.current) return;
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  }, [projectSlug]);

  useEffect(() => {
    loadSequence.current += 1;
    setConfig(null);
    setLoadedProject(undefined);
    setError(null);
  }, [projectSlug]);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * The navigation stays up in every state. A failed config read or a stale project slug is a
   * fact about the body, not about the screen; taking the nav away with it would strand the
   * operator on a dead end with no way to reach the browser or Nightshift categories, which
   * do not depend on that read at all.
   */
  let body: ReactNode;
  if (error && !config) {
    body = (
      <Empty
        title="Config could not be read."
        body={error}
        action={
          <button type="button" className="btn" onClick={() => void load()}>
            Try again
          </button>
        }
      />
    );
  } else if (!config || loadedProject !== projectSlug) {
    body = (
      <div style={{ display: 'grid', placeItems: 'center', flex: 1, minHeight: 200 }}>
        <Spinner size={20} />
      </div>
    );
  } else if (config.unknownProject) {
    /*
     * A slug nobody owns gets a dead end, not somebody else's settings. The server already
     * refuses to widen an unknown scope to every project; saying so here is what stops a stale
     * bookmark from reading as "this project has no configuration".
     */
    body = (
      <Empty
        title={`No project called "${projectSlug ?? ''}".`}
        body="The link may be stale, or the project may have been renamed. Pick a project from the header to see its settings."
      />
    );
  } else {
    body = (
      <SettingsBody
        tab={tab}
        projectSlug={projectSlug}
        user={user}
        canWrite={canWrite}
      />
    );
  }

  return (
    <div className="settings">
      <SettingsAutosaveNotice />
      <SettingsNav tab={tab} projectSlug={projectSlug} onSelect={setTab} />

      <div className="settings-main">
        <header className="settings-head">
          <div className="settings-crumb">
            <span className="kicker">{scopeMeta.label}</span>
            <span className="settings-target mono">{scopeTarget(meta.scope, projectSlug)}</span>
          </div>
          <h1 className="settings-title" id="settings-title">
            {meta.label}
          </h1>
          <p className="settings-lede">
            {meta.description}{' '}
            <span className="settings-reach">
              {scopeMeta.reach}
            </span>
          </p>
          {meta.scope === 'project' && projectSlug === null ? (
            <p className="settings-notice" role="status">
              No project is selected, so this shows values across every project. Pick a project in
              the header to edit its own settings.
            </p>
          ) : null}
        </header>

        <div className="settings-body" id="settings-panel" role="region" aria-labelledby="settings-title">
          <div className="cfg-wrap">{body}</div>
        </div>
      </div>
    </div>
  );
}

/* ── navigation ────────────────────────────────────────────────────────────── */

/**
 * Three sections, one per scope, each listing its categories. Wide screens get the list;
 * narrow ones get the same tree as a grouped select, so the grouping survives the collapse
 * instead of degrading to a flat row that overflows — which is the exact failure this
 * replaced.
 */
function SettingsNav({
  tab,
  projectSlug,
  onSelect,
}: {
  tab: TabId;
  projectSlug: string | null;
  onSelect: (tab: TabId) => void;
}) {
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    const items = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('.settings-nav-item'));
    const index = items.findIndex((item) => item === document.activeElement);
    if (index === -1) return;
    event.preventDefault();
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? items.length - 1
          : (index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
    items[next]?.focus();
  };

  return (
    <>
      <nav className="settings-nav" aria-label="Settings" onKeyDown={onKeyDown}>
        {SETTINGS_SCOPES.map((scope) => (
          <section className="settings-nav-group" key={scope.id} aria-labelledby={`settings-scope-${scope.id}`}>
            <h2 className="settings-nav-head" id={`settings-scope-${scope.id}`}>
              <span className="kicker">{scope.label}</span>
              <span className="settings-nav-target mono" title={scope.reach}>
                {scopeTarget(scope.id, projectSlug)}
              </span>
            </h2>
            <ul className="settings-nav-list">
              {settingsTabsForScope(scope.id).map((entry) => (
                <li key={entry.id}>
                  <button
                    type="button"
                    id={`settings-tab-${entry.id}`}
                    className="settings-nav-item"
                    aria-current={tab === entry.id ? 'page' : undefined}
                    onClick={() => onSelect(entry.id)}
                  >
                    {entry.label}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </nav>

      <label className="settings-nav-select">
        <span className="kicker">Section</span>
        <select className="input" value={tab} onChange={(event) => onSelect(event.target.value as TabId)}>
          {SETTINGS_SCOPES.map((scope) => (
            <optgroup key={scope.id} label={`${scope.label} · ${scopeTarget(scope.id, projectSlug)}`}>
              {settingsTabsForScope(scope.id).map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </label>
    </>
  );
}

/* ── body ──────────────────────────────────────────────────────────────────── */

function SettingsBody({
  tab,
  projectSlug,
  user,
  canWrite,
}: {
  tab: TabId;
  projectSlug: string | null;
  user: SessionUser | null;
  canWrite: boolean;
}) {
  switch (tab) {
    case 'prompts':
      return <PromptsPanel canWrite={canWrite} />;
    case 'project':
      return (
        <>
          <ProjectSettings key={projectSlug ?? 'all'} user={user} />
          {projectSlug ? (
            <ProjectConfigurationPanel key={`config-${projectSlug}`} projectSlug={projectSlug} user={user} sections={['identity', 'profile']} />
          ) : null}
        </>
      );
    case 'agents':
      return <ProjectConfigurationPanel key={projectSlug ?? 'all'} projectSlug={projectSlug} user={user} sections={['execution', 'tags', 'environment']} />;
    case 'pipeline':
      return <ProjectConfigurationPanel key={projectSlug ?? 'all'} projectSlug={projectSlug} user={user} sections={['commands', 'guidance', 'verification', 'artifacts']} />;
    case 'merge':
      return <ProjectConfigurationPanel key={projectSlug ?? 'all'} projectSlug={projectSlug} user={user} sections={['review', 'refine']} />;
    case 'performance':
      return <PerformanceTargetsPanel key={projectSlug ?? 'all'} user={user} />;
    case 'sidecars':
      return <SidecarsSettingsPanel key={projectSlug ?? 'all'} user={user} />;
    case 'milestones':
      return <MilestonesSettingsPanel key={projectSlug ?? 'all'} user={user} />;
    case 'models':
      return <ModelsSettingsPanel user={user} />;
    case 'routes':
      return <RoutesSettingsPanel user={user} />;
    case 'reviewers':
      return <ReviewersPanel user={user} />;
    case 'profiles':
      return <ProfilesPanel user={user} />;
    case 'machines':
      return <MachinesSettingsPanel user={user} />;
    case 'users':
      return <UsersPanel user={user} />;
    case 'connect':
      return <ConnectPanel user={user} />;
    case 'display':
      return <DisplayPanel />;
    case 'notifications':
      return <NotificationsPanel />;
    default: {
      /*
       * A category with no panel wired to it would otherwise render an empty pane under a
       * confident heading. The `never` binding turns that into a compile error the moment a
       * category is added to SETTINGS_TABS without one, and the fallback keeps a stale bundle
       * from showing blank space if it ever happens at runtime.
       */
      const unhandled: never = tab;
      return (
        <Empty
          title="This category has no editor yet."
          body={`Nothing is wired up for "${String(unhandled)}".`}
        />
      );
    }
  }
}

/* ── display options ───────────────────────────────────────────────────────── */

/**
 * Client-side, not config-table: scale is a property of the monitor you are looking at,
 * so it persists per browser and never round-trips through the orchestrator.
 */
function DisplayPanel() {
  const [scale, setScale] = useState(uiScale);
  const [times, setTimes] = useState(timeFormat);
  const [activity, setActivity] = useState(activityDisplay);

  return (
    <>
      <section className="sheet-panel">
        <header className="panel-head">
          <Kicker>Display</Kicker>
        </header>
        <div className="sheet-panel-body">
          <div className="cfg-field">
            <div style={{ minWidth: 0 }}>
              <span className="cfg-label">Interface scale</span>
              <p className="cfg-help">
                Scales the whole interface — type, spacing, everything. Takes effect immediately and
                is stored in this browser only, so each machine and monitor holds its own.
              </p>
            </div>
            <div className="cfg-control">
              <div className="seg">
                {UI_SCALE_OPTIONS.map((option) => (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={scale === option}
                    onClick={() => {
                      setUiScale(option);
                      setScale(option);
                    }}
                  >
                    {option}%
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="cfg-field">
            <div>
              <span className="cfg-label">Active tickets</span>
              <p className="cfg-help">Show a throbber or the elapsed time of the current run. Stored in this browser. Reduced motion keeps the throbber still.</p>
            </div>
            <div className="cfg-control"><div className="seg">
              {(['throbber', 'timer'] as const).map((option) => (
                <button key={option} type="button" aria-pressed={activity === option}
                  onClick={() => { setActivityDisplay(option); setActivity(option); }}>
                  {option === 'timer' ? 'Running timer' : 'Throbber'}
                </button>
              ))}
            </div></div>
          </div>
          <div className="cfg-field">
            <div style={{ minWidth: 0 }}>
              <span className="cfg-label">Times</span>
              <p className="cfg-help">
                Age is how long ago something happened. Local time is your clock, with the date
                when it is not today. Stored in this browser only.
              </p>
            </div>
            <div className="cfg-control">
              <div className="seg">
                {TIME_FORMATS.map((option) => (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={times === option}
                    onClick={() => {
                      setTimeFormat(option);
                      setTimes(option);
                    }}
                  >
                    {option === 'relative' ? 'Age' : 'Local time'}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

/**
 * Client-side, like scale: these toasts land on THIS machine, so the opt-in, quiet hours and
 * kind filters live in this browser rather than the orchestrator config table.
 */
function NotificationsPanel() {
  const store = useStore();
  const [prefs, setPrefs] = useState(backgroundNotificationPrefs);
  const [permission, setPermission] = useState(backgroundNotificationPermission);
  const [busy, setBusy] = useState(false);

  const apply = (patch: Parameters<typeof setBackgroundNotificationPrefs>[0]) => {
    setPrefs(setBackgroundNotificationPrefs(patch));
  };

  const onEnable = async (next: boolean) => {
    if (!next) {
      apply({ enabled: false });
      return;
    }
    setBusy(true);
    try {
      const granted = await requestBackgroundNotificationPermission();
      const current = backgroundNotificationPermission();
      setPermission(current);
      if (!granted) {
        apply({ enabled: false });
        store.toast(
          'bad',
          current === 'unsupported'
            ? 'This browser cannot raise desktop notifications.'
            : 'Desktop notifications were not allowed. The in-page toast, title and favicon still work.',
        );
        return;
      }
      apply({ enabled: true });
    } finally {
      setBusy(false);
    }
  };

  const quietStart = prefs.quietStartMinutes === null ? '' : formatClockMinutes(prefs.quietStartMinutes);
  const quietEnd = prefs.quietEndMinutes === null ? '' : formatClockMinutes(prefs.quietEndMinutes);

  const setQuiet = (which: 'start' | 'end', value: string) => {
    const parsed = value === '' ? null : parseClockMinutes(value);
    const start = which === 'start' ? parsed : prefs.quietStartMinutes;
    const end = which === 'end' ? parsed : prefs.quietEndMinutes;
    apply({ quietStartMinutes: start, quietEndMinutes: end });
  };

  return (
    <section className="sheet-panel">
      <header className="panel-head">
        <Kicker>Background notifications</Kicker>
      </header>
      <div className="sheet-panel-body">
        <div className="cfg-field">
          <div style={{ minWidth: 0 }}>
            <span className="cfg-label">Desktop toasts</span>
            <p className="cfg-help">
              Off until you ask. When on, Windows (or the browser) shows a notification for Human
              Review and failures even if this tab is in the background. Same ticket is not pinged
              twice. Does not post to GitLab.
              {permission === 'denied'
                ? ' The browser has blocked them for this origin.'
                : permission === 'unsupported'
                  ? ' This browser has no Notification API.'
                  : ''}
            </p>
          </div>
          <div className="cfg-control">
            <button
              type="button"
              className="btn btn-sm"
              disabled={busy}
              aria-pressed={prefs.enabled}
              onClick={() => void onEnable(!prefs.enabled)}
              style={{ color: prefs.enabled ? 'var(--good-text)' : undefined }}
            >
              {busy ? <Spinner /> : null}
              {prefs.enabled ? 'on' : 'off'}
            </button>
          </div>
        </div>

        <div className="cfg-field">
          <div style={{ minWidth: 0 }}>
            <span className="cfg-label">Human Review</span>
            <p className="cfg-help">A ticket reached Human Review and is waiting for your merge.</p>
          </div>
          <div className="cfg-control">
            <button
              type="button"
              className="btn btn-sm"
              disabled={!prefs.enabled}
              aria-pressed={prefs.humanReview}
              onClick={() => apply({ humanReview: !prefs.humanReview })}
              style={{ color: prefs.humanReview ? 'var(--good-text)' : undefined }}
            >
              {prefs.humanReview ? 'on' : 'off'}
            </button>
          </div>
        </div>

        <div className="cfg-field">
          <div style={{ minWidth: 0 }}>
            <span className="cfg-label">Failures</span>
            <p className="cfg-help">
              Escalations, bounce-cap, failed runs, orphaned attempts and risky CI gates.
            </p>
          </div>
          <div className="cfg-control">
            <button
              type="button"
              className="btn btn-sm"
              disabled={!prefs.enabled}
              aria-pressed={prefs.failures}
              onClick={() => apply({ failures: !prefs.failures })}
              style={{ color: prefs.failures ? 'var(--good-text)' : undefined }}
            >
              {prefs.failures ? 'on' : 'off'}
            </button>
          </div>
        </div>

        <div className="cfg-field">
          <div style={{ minWidth: 0 }}>
            <span className="cfg-label">Quiet hours</span>
            <p className="cfg-help">
              Local time on this machine. Leave empty for none. A start later than the end wraps
              midnight — 22:00 until 07:00 is the overnight window.
            </p>
          </div>
          {/* One range, so it breaks as one: `start until end` never splits across two lines. */}
          <div className="cfg-control cfg-range">
            <input
              className="input"
              type="time"
              aria-label="Quiet hours start"
              value={quietStart}
              disabled={!prefs.enabled}
              onChange={(event) => setQuiet('start', event.target.value)}
              style={{ maxWidth: 120 }}
            />
            <span className="dim" style={{ fontSize: 'var(--fs-micro)' }}>
              until
            </span>
            <input
              className="input"
              type="time"
              aria-label="Quiet hours end"
              value={quietEnd}
              disabled={!prefs.enabled}
              onChange={(event) => setQuiet('end', event.target.value)}
              style={{ maxWidth: 120 }}
            />
          </div>
        </div>

        <div className="cfg-field">
          <div style={{ minWidth: 0 }}>
            <span className="cfg-label">While this tab is open</span>
            <p className="cfg-help">
              Skip the desktop toast when you are already looking at the dashboard. The in-page
              toast, title badge and chime still fire.
            </p>
          </div>
          <div className="cfg-control">
            <button
              type="button"
              className="btn btn-sm"
              disabled={!prefs.enabled}
              aria-pressed={prefs.suppressWhenVisible}
              onClick={() => apply({ suppressWhenVisible: !prefs.suppressWhenVisible })}
              style={{ color: prefs.suppressWhenVisible ? 'var(--good-text)' : undefined }}
            >
              {prefs.suppressWhenVisible ? 'skip' : 'also toast'}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
